import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';
import Sparkline from '../components/Sparkline';

type TenantStatus = 'provisioning' | 'active' | 'suspended' | 'trial' | 'deleted';

interface FleetTenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  createdAt: string;
  plan: { id: string; slug: string; name: string } | null;
  slackConnected: boolean;
  teamsConnected: boolean;
  lastActivityAt: string | null;
  usageSeries: {
    reportsSubmitted: number[];
    deliveriesSent: number[];
    deliveryFailures: number[];
    failureRatePct: number | null;
  };
}

interface FleetResponse {
  data: { tenants: FleetTenant[] };
}

const STATUS_BADGE: Record<TenantStatus, string> = {
  provisioning: 'bg-amber-100 text-amber-800',
  active:       'bg-emerald-100 text-emerald-800',
  trial:        'bg-amber-100 text-amber-800',
  suspended:    'bg-red-100 text-red-800',
  deleted:      'bg-slate-200 text-slate-700',
};

// Order matters — status chips render in this order.
const STATUS_FILTERS: TenantStatus[] = ['provisioning', 'active', 'suspended'];
// Platform filter values: 'any' means no platform filter; 'none' means
// tenants with NO platform connected (a common triage view).
type PlatformFilter = 'all' | 'slack' | 'teams' | 'none';
const PLATFORM_FILTERS: Array<{ key: PlatformFilter; label: string }> = [
  { key: 'all', label: 'All platforms' },
  { key: 'slack', label: 'Slack connected' },
  { key: 'teams', label: 'Teams connected' },
  { key: 'none', label: 'No platform' },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 60 * 1000) return 'just now';
  if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / (60 * 60 * 1000))}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return formatDate(iso);
}

// Buckets the raw failure rate into a color band. Kept generous — anything
// below 5% is "green" territory since Slack API blips are normal.
function failureRateClass(pct: number | null): string {
  if (pct === null) return 'text-slate-400';
  if (pct >= 25) return 'text-red-700 font-medium';
  if (pct >= 5) return 'text-amber-700';
  return 'text-emerald-700';
}

export default function Fleet() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery<FleetResponse>({
    queryKey: ['tenants', 'fleet'],
    queryFn: async () => {
      const token = getStoredToken();
      const res = await fetch(apiPath('/api/tenants/fleet'), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`fleet fetch failed: ${res.status}`);
      return res.json();
    },
  });

  const [statusFilters, setStatusFilters] = useState<Set<TenantStatus>>(new Set());
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');

  function toggleStatus(s: TenantStatus) {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.data.tenants.filter((t) => {
      if (statusFilters.size > 0 && !statusFilters.has(t.status)) return false;
      switch (platformFilter) {
        case 'slack':
          return t.slackConnected;
        case 'teams':
          return t.teamsConnected;
        case 'none':
          return !t.slackConnected && !t.teamsConnected;
        case 'all':
        default:
          return true;
      }
    });
  }, [data, statusFilters, platformFilter]);

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Fleet Overview</h1>
          <p className="mt-1 text-sm text-slate-600">
            All tenants on PowerMove. Click a row to drill into tenant detail.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {data && (
            <div className="text-sm text-slate-500">
              {filtered.length} of {data.data.tenants.length} tenant
              {data.data.tenants.length === 1 ? '' : 's'}
            </div>
          )}
          <Link
            to="/provisioning/new"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            New tenant
          </Link>
        </div>
      </div>

      {/* Filter chips */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Status</span>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium transition',
                statusFilters.has(s)
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {s}
            </button>
          ))}
          {statusFilters.size > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilters(new Set())}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Clear
            </button>
          )}
        </div>
        <div className="mx-3 h-4 w-px bg-slate-200" aria-hidden />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Platform</span>
          {PLATFORM_FILTERS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPlatformFilter(p.key)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium transition',
                platformFilter === p.key
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Loading tenants…
        </div>
      )}
      {isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Could not load tenants: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      )}
      {data && data.data.tenants.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No tenants yet.
        </div>
      )}
      {data && data.data.tenants.length > 0 && filtered.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No tenants match the current filters.
        </div>
      )}
      {data && filtered.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tenant</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Platforms</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Plan</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Reports 7d</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Deliveries 7d</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Failure rate</th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((t) => {
                const totalReports = t.usageSeries.reportsSubmitted.reduce((a, b) => a + b, 0);
                const totalDeliveries = t.usageSeries.deliveriesSent.reduce((a, b) => a + b, 0);
                return (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/fleet/${t.id}`)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-slate-900">{t.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{t.slug}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', STATUS_BADGE[t.status])}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1.5">
                        <PlatformPill label="Slack" on={t.slackConnected} />
                        <PlatformPill label="Teams" on={t.teamsConnected} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {t.plan ? t.plan.name : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <Sparkline
                          values={t.usageSeries.reportsSubmitted}
                          strokeClass="stroke-primary-500"
                          fillClass="fill-primary-600"
                        />
                        <span className="text-xs text-slate-500 tabular-nums">{totalReports}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <Sparkline
                          values={t.usageSeries.deliveriesSent}
                          strokeClass="stroke-emerald-500"
                          fillClass="fill-emerald-600"
                        />
                        <span className="text-xs text-slate-500 tabular-nums">{totalDeliveries}</span>
                      </div>
                    </td>
                    <td className={clsx('px-4 py-3 text-sm tabular-nums', failureRateClass(t.usageSeries.failureRatePct))}>
                      {t.usageSeries.failureRatePct === null ? '—' : `${t.usageSeries.failureRatePct}%`}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatRelative(t.lastActivityAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlatformPill({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
        on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500 line-through',
      )}
      title={on ? `${label} connected` : `${label} not connected`}
    >
      {label}
    </span>
  );
}
